import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { AuthService } from '../../shared/service/auth-service';
import { SecureAuthService } from '../../shared/service/secure-auth-service';
import { Router } from '@angular/router';
import { ToasterCommonService } from '../../shared/service/toaster-common-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { EncryptionService } from '../../shared/service/encryption-service';
import { BrowserStorageService } from '../../shared/service/browser-storage-service';
import { SidebarService } from '../../shared/service/sidebar-service';
import { InputValidationService } from '../../shared/service/input-validation-service';
import { TimezoneService } from '../../shared/service/timezone-service';
import { ThemeService } from '../../shared/service/theme-service';


@Component({
  selector: 'app-sign-in',
  standalone: false,
  templateUrl: './sign-in.html',
  styleUrl: './sign-in.scss'
})
export class SignIn implements OnInit {
  private browserStorage = inject(BrowserStorageService);
  private encryptionService = inject(EncryptionService);
  private timezoneService = inject(TimezoneService);
  public themeService = inject(ThemeService);

  loginForm!: FormGroup;
  password_show_and_hide = false;
  currentYear: number = new Date().getFullYear();
  loading = false;
  
  constructor(
    private authService: AuthService,
    private secureAuthService: SecureAuthService,
    private router: Router,
    public spinner: NgxSpinnerService,
    private toastr: ToasterCommonService,
    private sidebarService: SidebarService,
    private inputValidation: InputValidationService
  ) {}

  // Enhanced email validator with security checks
  private emailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }
    
    // Use the comprehensive validation service
    const validationResult = this.inputValidation.validateAndSanitize(control.value, 'email');
    
    if (!validationResult.isValid) {
      return { 
        invalidEmail: true, 
        securityErrors: validationResult.errors 
      };
    }
    
    return null;
  }

  // Password security validator
  private passwordSecurityValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const validationResult = this.inputValidation.validateAndSanitize(control.value, 'text');
    
    if (!validationResult.isValid) {
      return { 
        securityViolation: true, 
        securityErrors: validationResult.errors 
      };
    }

    return null;
  }

  private getFormValidationErrors(): string[] {
    const errors: string[] = [];
    
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      if (control && control.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          const error = control.errors![errorKey];
          switch (errorKey) {
            case 'required':
              errors.push(`${key.charAt(0).toUpperCase() + key.slice(1)} is required`);
              break;
            case 'email':
              errors.push('Please enter a valid email address');
              break;
            case 'securityViolation':
              errors.push(error.message || 'Security violation detected');
              break;
            case 'weakPassword':
              errors.push(error.message || 'Password does not meet security requirements');
              break;
            case 'invalidInput':
              errors.push(error.message || 'Invalid input detected');
              break;
            default:
              errors.push(`${key} is invalid`);
          }
        });
      }
    });
    
    return errors;
  }

  ngOnInit() {
    this.loginForm = new FormGroup({
      email: new FormControl('', [
        Validators.required, 
        this.emailValidator.bind(this),
        this.inputValidation.secureEmailValidator()
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.maxLength(128),
        this.passwordSecurityValidator.bind(this)
      ]),
      timestamp: new FormControl(''),
      check_button: new FormControl(''),
    });
    this.browserStorage.setSessionItem("loggedIn", 'false');
  }

  onEmailPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const trimmed = pastedText.trimStart();
    const control = this.loginForm.get('email');
    control?.setValue(trimmed);
    control?.markAsTouched();
  }

  onEmailInput() {
    const control = this.loginForm.get('email');
    const value = control?.value || '';
    if (value !== value.trimStart()) {
      control?.setValue(value.trimStart(), { emitEvent: false });
    }
  }

  loginCheck() {
    if (!this.loginForm.valid) {
      const errors = this.getFormValidationErrors();
      this.toastr.showError(errors.join('. '), 'Validation Error');
      return;
    }

    this.loading = true;
    this.spinner.show();

    const emailValidation = this.inputValidation.validateAndSanitize(
      this.loginForm.value.email?.trim(),
      'email'
    );
    const passwordValidation = this.inputValidation.validateAndSanitize(
      this.loginForm.value.password?.trim(),
      'text'
    );

    this.secureAuthService.secureLogin({
      email: emailValidation.sanitizedValue,
      password: passwordValidation.sanitizedValue,
      encryptionBypass: true
    }).subscribe({
      next: (loginResult) => {
        this.loading = false;
        this.spinner.hide();

        const loginData = loginResult?.data;
        const emailToStore = loginData?.email ?? emailValidation.sanitizedValue;
        const tempChangedFlag = loginData?.is_temporary_password_changed;

        // Status 203 or temp password not changed → force password change
        if (loginResult.status === 203 || (loginResult.success && !tempChangedFlag)) {
          this.loginForm.reset();
          this.browserStorage.setSessionItem('email', emailToStore);
          this.router.navigateByUrl('auth/first-time-change-password', { replaceUrl: true });
          return;
        }

        if (!loginResult.success) {
          this.toastr.showError(loginResult.message || 'Login failed', '');
          return;
        }

        // Success with temp password already changed → proceed to dashboard
        const strForText = String(loginData?.str_for ?? '').trim();
        const allParamsText = String(loginData?.all_params ?? '').trim();
        const validOrNot = loginData?.valid_or_not;
        const tenant = loginData?.tenant;
        const hasValidPayload = allParamsText && allParamsText !== 'undefined' && allParamsText !== 'null';

        if (!strForText || !hasValidPayload || validOrNot === undefined || tenant === undefined) {
          this.toastr.showError(loginResult?.message || 'Login failed', '');
          return;
        }

        this.browserStorage.setSessionItem('str_for', strForText);
        this.browserStorage.setSessionItem('app_params', this.encryptionService.encryptionAES(allParamsText));
        this.browserStorage.setSessionItem('loggedIn', String(validOrNot));
        this.browserStorage.setSessionItem('tenant', String(tenant));
        this.browserStorage.setSessionItem('side', this.encryptionService.encryptionAES(String(Number(loginData?.sideMenu ?? loginData?.side_menu ?? -1))));
        this.timezoneService.setTimezone(loginData?.user_timezone || this.timezoneService.detectBrowserTimezone());

        const userType = Number(loginData?.user_type);
        const successMessage = loginData?.message ?? loginResult?.message;
        this.toastr.showSuccess(successMessage || 'Login successful', '');
        this.loginForm.reset();

        if (userType === 0) {
          this.sidebarService.setSidebar('tenant');
          this.router.navigateByUrl('super-admin/all-tenats', { replaceUrl: true });
        } else {
          this.sidebarService.setSidebar('main');
          this.router.navigateByUrl('all-events', { replaceUrl: true });
        }

        setTimeout(() => this.authService.startSessionMonitor(), 0);
      },
      error: (error) => {
        this.loading = false;
        this.spinner.hide();

        const errorMessage = typeof error === 'string' ? error :
          error?.message || error?.error?.message || 'Login failed';

        if (errorMessage === 'ACCOUNT_LOCKED' || errorMessage.includes?.('Account locked')) {
          this.toastr.showError('Account temporarily locked due to multiple failed attempts. Please try again later.', '');
        } else {
          this.toastr.showError('Login failed. Please check your credentials.', '');
        }
      }
    });
  }

  signUp() {
    this.router.navigate(['auth','sign-up'], {
      replaceUrl: true,
    });
  }
  goToHome() {
    return window.location.href = 'https://confocean.com';
  }
}

